import * as turf from '@turf/turf'
import { jStat } from 'jstat'
import { uniqBy } from 'lodash'
import proj4 from 'proj4'
import { computed, ref, watch } from 'vue'
const WSG84_PROJECTION = '+proj=longlat +ellps=WGS84 +datum=WGS84 +no_defs'
const UTM_PROJECTION = '+proj=utm +zone=10 +ellps=WGS84 +datum=WGS84 +units=m +no_defs'

import {
	getPlanejamentoAmostrasSingleTrees,
	getPlanejamentoDeInventarioFlorestalContinuo,
	getPlanejamentoDeInventarioFlorestalInstalacao,
	getPlanejamentoDeInventarioFlorestalPreCorte,
} from '../services/requests/OrdemDeServicoRequests'
import { useOrdemServico } from './useOrdemServico'

const { ordemServico, initStatePlanejamento } = useOrdemServico()

const responsePlanejamento = ref(undefined)
const parcelasGeradas = ref([])

export const usePlanejamentoOrdemServico = () => {
	const { geoJsonFazenda, geoJsonPlantios, parseGeoJson } = useGeoJson()

	watch(responsePlanejamento, () => {
		parseGeoJson(responsePlanejamento.value.maps[0])
	})

	function getPlanejamentoOrdemServico() {
		const relacao = {
			'001': getPlanejamentoDeInventarioFlorestalInstalacao,
			'002': getPlanejamentoDeInventarioFlorestalContinuo,
			'003': getPlanejamentoDeInventarioFlorestalPreCorte,
			'007': getPlanejamentoAmostrasSingleTrees,
		}

		relacao[ordemServico.value.orsv_cd_tipo_servico](
			ordemServico.value.id,
			ordemServico.value.fk_esfl_estrato_florestal,
		).then((response) => {
			console.log('🚀 ~ getPlanejamentoOrdemServico ~ response:', response)
			responsePlanejamento.value = response.data.result
		})
	}

	//TODO para ordens de serviços que reutilizam parcelas, popular esta lista para que cálculo fique correto
	const parcelasExistentes = ref([])

	const bufferBorda = computed(() => {
		const radius =
			(ordemServico.value.orsv_nr_buffer > 0
				? ordemServico.value.orsv_nr_buffer * -1
				: ordemServico.value.orsv_nr_buffer) / 1609.344

		if (radius) {
			return turf.featureCollection(
				geoJsonPlantios.value.features.map((feature) =>
					turf.buffer(feature, radius, { units: 'miles' }),
				),
			)
		}

		return turf.featureCollection([])
	})

	/**
	 * areaEstrato: Retorna área do estrato dado soma das áreas dos plantios
	 * ! Presume-se que 'plantios' faz parte de um estrato
	 * ! áreas são calculadas através da função 'area' do turf
	 */
	const areaEstrato = computed(() => {
		if (bufferBorda.value.features.length) {
			return (
				bufferBorda.value.features
					?.map((feature) => turf.area(feature))
					.reduce((a, b) => a + b, 0) / 10000 || 0
			)
		}

		return (
			geoJsonPlantios.value.features
				?.map((feature) => turf.area(feature))
				.reduce((a, b) => a + b, 0) / 10000 || 0
		)
	})

	const coeficienteVariacao = computed(() => {
		const ns = 0.05
		let n = ordemServico.value.orsv_nr_amostra_referencia

		for (let i = 0; i < 50; i++) {
			n =
				(Math.pow(jStat.studentt.inv(ns, n - 1), 2) * Math.pow(ordemServico.value.orsv_nr_cv, 2)) /
				(Math.pow(ordemServico.value.orsv_nr_error_target, 2) +
					(Math.pow(jStat.studentt.inv(ns, n - 1), 2) *
						Math.pow(ordemServico.value.orsv_nr_cv, 2)) /
						areaEstrato.value)
		}

		return n
	})

	/**
	 * Calcula o campo de intensidade amostral (Hectare/Unidade)
	 * cálculo: área do estrato (area de todos as florestas somadas) dividido pelo numero de intensidade amostral
	 */
	const hectaresPorParcela = computed({
		get() {
			if (!ordemServico.value.orsv_is_cv) {
				return ordemServico.value.orsv_nr_amostra_total
					? Number((areaEstrato.value / ordemServico.value.orsv_nr_amostra_total).toFixed(1))
					: ordemServico.value.orsv_nr_intensidade_amostral
			}

			return Math.ceil(areaEstrato.value / coeficienteVariacao.value) || 0
		},
		set(value) {
			ordemServico.value.orsv_nr_intensidade_amostral = value
		},
	})

	/**
	 * Calcula o campo de total de paprcelas
	 * cálculo: área do estrato (area de todos os plantios somados) dividida pela intensidade amostral
	 */
	const totalDeParcelas = computed({
		get() {
			if (!coeficienteVariacao.value) {
				return parcelasExistentes.value.length
					? Number(
							(areaEstrato.value / ordemServico.value.orsv_nr_intensidade_amostral).toFixed(),
						) - ordemServico.value.orsv_nr_amostra_total
					: Number(
							(areaEstrato.value / ordemServico.value.orsv_nr_intensidade_amostral).toFixed(),
						) || ordemServico.value.orsv_nr_amostra_total
			}

			return Number(coeficienteVariacao.value.toFixed() || 0)
		},
		set(value) {
			ordemServico.value.orsv_nr_amostra_total = value
		},
	})

	const geojson = computed(() => {
		if (!geoJsonPlantios.value && !geoJsonTalhoes.value) {
			return turf.featureCollection([])
		}
		return turf.featureCollection([
			geoJsonFazenda.value,
			...geoJsonPlantios.value.features,
			...bufferBorda.value.features,
		]) //, ...geoJsonTalhoes.valuegeoJsonPlantios])
	})

	const bbox = computed(() => {
		return turf.bbox(bufferBorda.value.features.length ? bufferBorda.value : geoJsonPlantios.value)
	})

	const parcelasAleatorias = () => {
		const parcelasGeradas = []
		const numeroParcelasTotal = totalDeParcelas.value
		console.log(
			'🚀 ~ file: usePlanejamentoOrdemServico.ts:154 ~ parcelasAleatorias ~ numeroParcelasTotal:',
			numeroParcelasTotal,
		)
		const collection = ordemServico.value.orsv_nr_buffer ? bufferBorda.value : geoJsonPlantios.value

		while (parcelasGeradas.length < numeroParcelasTotal) {
			const parcelaAleatoria = turf.randomPoint(1, { bbox: bbox.value })
			const estaNosPlantios = collection.features.some((plantio) =>
				turf.booleanPointInPolygon(parcelaAleatoria.features[0], plantio),
			)

			if (estaNosPlantios) {
				parcelasGeradas.push(parcelaAleatoria.features[0])
			}
		}

		return parcelasGeradas
	}

	function coordenadaAleatoria() {
		const multiplicadorLatitude = 0.000898316
		const multiplicadorLongitude = 0.000959004

		const latitudeAleatoria =
			Math.random() * (Math.floor(Math.random() * 2) == 1 ? 1 : -1) * multiplicadorLatitude
		const longitudeAleatoria =
			Math.random() * (Math.floor(Math.random() * 2) == 1 ? 1 : -1) * multiplicadorLongitude

		return { longitudeAleatoria, latitudeAleatoria }
	}

	function coordenadaAleatorizadaPontoSuperior(coordenada) {
		const { longitudeAleatoria, latitudeAleatoria } = coordenadaAleatoria()

		const lng = longitudeAleatoria > 0 ? longitudeAleatoria : longitudeAleatoria * -1
		const lat = latitudeAleatoria < 0 ? latitudeAleatoria : latitudeAleatoria * -1

		return {
			latitude: (coordenada.latitude * -1 + lat) * -1,
			longitude: (coordenada.longitude * -1 + lng) * -1,
		}
	}

	function coordenadaAleatorizadaPontoInferior(coordenada) {
		const { longitudeAleatoria, latitudeAleatoria } = coordenadaAleatoria()

		const lng = longitudeAleatoria < 0 ? longitudeAleatoria : longitudeAleatoria * -1
		const lat = latitudeAleatoria > 0 ? latitudeAleatoria : latitudeAleatoria * -1

		return {
			latitude: (coordenada.latitude * -1 + lat) * -1,
			longitude: (coordenada.longitude * -1 + lng) * -1,
		}
	}

	function getExtremosAleatorizados(coordenadas) {
		const [lat_inf, lng_inf] = turf.getCoord(coordenadas[0])
		const { latitude: lat_inferior, longitude: lng_inferior } = coordenadaAleatorizadaPontoInferior(
			{ latitude: lat_inf, longitude: lng_inf },
		)

		const [lat_sup, lng_sup] = turf.getCoord(coordenadas[1])
		const { latitude: lat_superior, longitude: lng_superior } = coordenadaAleatorizadaPontoSuperior(
			{ latitude: lat_sup, longitude: lng_sup },
		)

		const pontoSuperior = turf.point([lat_superior, lng_superior])
		const pontoInferior = turf.point([lat_inferior, lng_inferior])

		return turf.bbox(turf.featureCollection([pontoInferior, pontoSuperior]))
	}

	function pontosBbox() {
		const coordenadasBbox = turf.getCoords(
			turf.bboxPolygon(
				turf.bbox(bufferBorda.value.features.length ? bufferBorda.value : geoJsonPlantios.value),
			),
		)

		const aleat = getExtremosAleatorizados([coordenadasBbox[0][1], coordenadasBbox[0][3]])

		return aleat
	}

	function squareGrid() {
		return turf.squareGrid(pontosBbox(), distanciaEntreUnidades.value, {
			units: 'miles',
		})
	}

	const distanciaEntreUnidades = computed(() => {
		var times = 0
		if (!parcelasExistentes.value.length) {
			times = hectaresPorParcela.value * 10000
		} else {
			times = (areaEstrato.value / totalDeParcelas.value) * 10000
		}
		var distancia = Math.sqrt(times)
		var cellSide = distancia / 1609.344

		return cellSide
	})

	function parcelasSistematicas() {
		const collection = ordemServico.value.orsv_nr_buffer ? bufferBorda.value : geoJsonPlantios.value

		return uniqBy(
			squareGrid()
				.features.flatMap((feature) => turf.getCoords(feature))
				.flat(),
			([lat, lng]) => lat + lng,
		)
			.map(([longitude, latitude]) => {
				if (ordemServico.value.orsv_tx_metodologia_amostral != 'Sistematica') {
					const random = Math.random() * ((distanciaEntreUnidades.value * 1609.344) / 2)
					const [utm_latitude, utm_longitude] = proj4(WSG84_PROJECTION, UTM_PROJECTION, [
						latitude,
						longitude,
					])

					const utm_lat = utm_latitude + random
					const utm_lng = utm_longitude + random

					const [wsg_latitude, wsg_longitude] = proj4(UTM_PROJECTION, WSG84_PROJECTION, [
						utm_lat,
						utm_lng,
					])

					return turf.point([wsg_longitude, wsg_latitude])
				}

				return turf.point([longitude, latitude])
			})
			.filter((ponto) =>
				collection.features.some((plantio) => turf.booleanPointInPolygon(ponto, plantio)),
			)
	}

	function gerarParcelas() {
		return ordemServico.value.orsv_tx_metodologia_amostral === 'Aleatoria'
			? parcelasAleatorias()
			: parcelasSistematicas()
	}

	function lancarParcelas() {
		let parcelas = []
		while (parcelas.length != totalDeParcelas.value) {
			parcelas = gerarParcelas()
		}

		parcelasGeradas.value = parcelas
	}

	function limparPlanejamento() {
		ordemServico.value = Object.assign(ordemServico.value, initStatePlanejamento)
		parcelasGeradas.value = []
	}

	return {
		hectaresPorParcela,
		totalDeParcelas,
		ordemServico,
		geoJsonPlantios,
		getPlanejamentoOrdemServico,
		geojson,
		gerarParcelas,
		lancarParcelas,
		parcelasGeradas,
		limparPlanejamento,
		initStatePlanejamento,
		areaEstrato,
	}
}

const useGeoJson = () => {
	function paintFeatures(features, cor = '#FEFEFE') {
		return features.map((feature) => {
			feature.properties = {
				...feature.properties,
				cor,
			}
			return feature
		})
	}

	const geoJsonPlantios = ref(turf.featureCollection([]))
	const geoJsonTalhoes = ref(turf.featureCollection([]))
	const geoJsonFazenda = ref(turf.feature({}))

	function parseGeoJson({ fazenda = null, plantios = null, talhoes = null }) {
		geoJsonFazenda.value = JSON.parse(fazenda)
		geoJsonPlantios.value = JSON.parse(plantios)
		geoJsonPlantios.value.features = paintFeatures(geoJsonPlantios.value.features, '#77F821')
		geoJsonTalhoes.value = JSON.parse(talhoes)
	}

	return {
		geoJsonPlantios,
		geoJsonFazenda,
		geoJsonTalhoes,
		parseGeoJson,
	}
}
